import { type FC, lazy, Suspense, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { createSelector } from '@reduxjs/toolkit';

import { _isEmpty, _isNil, _isNotEmpty, _notNil } from '@/littledash';

import { AnimalSexLabelToCode } from '@/model/Animal.utils';
import type { AnimalSex, AnimalSexLabel } from '@/model/Animal.model';
import type { Animal, AnimalSpecies } from '@/model/Animal.model';
import type { ID } from '@/model/Common.model';
import type { MetadataField, MetadataFieldValueTypes } from '@/model/Metadata.model';
import { MetadataTypes } from '@/model/Metadata.model';

import type {
  CustomColumnSettings,
  CustomGridSettings,
  CustomValidation,
} from '@/components/UI/SpreadSheet/SpreadSheet.model';
import type { SpreadsheetV16Settings } from '@/components/UI/SpreadSheet/SpreadsheetV16.model';
import type { HotTableRef } from '@/components/UI/SpreadSheet/HotTableV16';
import { buildCustomValidators, spreadSheetValidationTooltip } from '@/components/UI/SpreadSheet/SpreadSheet.utils';
import { createMetaMap } from '@/support/hot';
import { createMetaV16 } from '@/support/hot-v16';

import { ApiService } from '@/support/ApiService';
import { web as webRoute } from '@/support/route';
import { useApiHook } from '@/support/Hooks/api/useApiHook.ts';
import { useEntitySchemaFieldsByType } from '@/utils/entitySchema/useEntitySchema';
import { featuresSelector } from '@/support/Selectors';

import ApiErrorBanner from '@/components/ApiErrorBanner';
import Loading from '@/components/Loading';
import NoDataCard from '@/components/NoDataCard';
import Button from '@/components/UI/Button';
import Header from '@/components/UI/Header';
import Link from '@/components/UI/Link';
import SubHeader from '@/components/UI/SubHeader';
import { successToast, toNumericId } from '@/helpers';

// @ts-expect-error — v12 @handsontable/react has no type declarations
import type HotTable from '@handsontable/react';
import type { CellChange } from 'handsontable/common';

import {
  createStrainResolver,
  getMetaColumns,
  initAnimalSettings,
  initialBulkAnimalData,
  updateStrainSelectDropdown,
} from '../Colony.utils';
import { initAnimalSettingsV16, initialBulkAnimalDataV16 } from '../Colony.utils.v16';
import '../Colony.scss';

const SpreadSheet = lazy(() => import('@/components/UI/SpreadSheet'));
const BulkAnimalsTableV16 = lazy(() => import('./BulkAnimalsTableV16'));

interface BulkAnimalRow {
  id: ID;
  catalog: string;
  name: string;
  dob: string | null;
  cage_catalog: string;
  cage_name: string;
  sex?: string;
  species?: string;
  strain?: string;
  meta: Record<string, MetadataFieldValueTypes>;
}

interface AnimalPatchData {
  id: number;
  cage_id: number | null;
  name: string;
  dob: string | null;
  sex?: AnimalSex;
  strain_id?: number | null;
  meta?: Record<string, MetadataFieldValueTypes>;
}

interface AnimalSettingsResult {
  settings: CustomGridSettings;
  metadata?: Array<MetadataField>;
}

interface AnimalSettingsResultV16 {
  settings: SpreadsheetV16Settings;
  metadata?: Array<MetadataField>;
}

const hotUpgradeSelector = createSelector([featuresSelector], (features) => ({
  hotUpgrade: features?.hot_upgrade ?? false,
  unknownSexEnabled: features?.unknown_sex === true,
}));

const BulkAnimalEdit: FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { hotUpgrade, unknownSexEnabled } = useSelector(hotUpgradeSelector);

  const refV12 = useRef<HotTable>(null);
  const refV16 = useRef<HotTableRef>(null);

  const [data, setData] = useState<Array<BulkAnimalRow>>([]);
  const [settings, setSettings] = useState<CustomGridSettings | SpreadsheetV16Settings | null>(null);
  const [apiError, setApiError] = useState<Error | false>(false);
  const [metadata, setMetadata] = useState<Array<MetadataField>>([]);
  const [loading, setLoading] = useState(false);
  const [isInvalid, setIsInvalid] = useState<boolean>(false);
  const entityFieldRequiredMap = useEntitySchemaFieldsByType('animal');
  const { id: studyId } = useParams();

  const selectedAnimals: Array<Animal> = useMemo(
    () => (location.state as { animals?: Animal[] })?.animals ?? [],
    [location.state]
  );

  useEffect(() => {
    if (_isEmpty(selectedAnimals)) {
      navigate(_notNil(studyId) ? webRoute('studies.animals', { id: studyId }) : '/colony', {
        replace: true,
        state: { tab: 1 },
      });
    }
  }, [selectedAnimals, navigate, studyId]);

  const { response: animalMetadataResponse, loading: animalMetadataLoading } = useApiHook({
    endpoint: 'GET /api/v1/metadata-glossary',
    invokeOnInit: true,
    query: { filter_type: MetadataTypes.subject_meta },
    options: {
      onError: {
        throw: false,
        capture: true,
        toast: false,
        slug: 'bulk-animal-edit-fetch-metadata',
      },
    },
  });

  const { response: speciesResponse, loading: speciesLoading } = useApiHook({
    endpoint: 'GET /api/v1/species',
    invokeOnInit: true,
    query: { include: ['strains'] },
    options: { onError: { throw: false, capture: true, toast: false, slug: 'bulk-animal-edit-fetch-species' } },
  });

  const metaColumns = useMemo(() => {
    const metadataResponseData = (animalMetadataResponse?.body ?? []) as Array<MetadataField>;
    if (_isNotEmpty(metadataResponseData) && _isNotEmpty(selectedAnimals)) {
      return getMetaColumns(selectedAnimals, metadataResponseData);
    }
    return [];
  }, [animalMetadataResponse, selectedAnimals]);

  const initialLoad =
    _isNil(animalMetadataResponse?.body) || _isNil(speciesResponse?.body) || animalMetadataLoading || speciesLoading;
  const species = useMemo<Array<AnimalSpecies>>(
    () => (speciesResponse?.body?.data ?? []) as unknown as Array<AnimalSpecies>,
    [speciesResponse]
  );
  const availableMetadata = useMemo<Array<MetadataField>>(
    () => (animalMetadataResponse?.body ?? []) as Array<MetadataField>,
    [animalMetadataResponse]
  );

  const customValidators = useMemo<Record<string, CustomValidation>>(() => {
    if (hotUpgrade) {
      // V16 handles validators internally in SpreadsheetV16
      return {};
    }
    return buildCustomValidators((settings as CustomGridSettings)?.columns ?? []);
  }, [settings, hotUpgrade]);

  useEffect(() => {
    if (!initialLoad && _notNil(metaColumns) && _isNil(settings) && _isNotEmpty(selectedAnimals)) {
      const removeRow = false;

      if (hotUpgrade) {
        const newSettings = initAnimalSettingsV16(
          species,
          true,
          removeRow,
          metaColumns,
          availableMetadata,
          undefined,
          entityFieldRequiredMap,
          unknownSexEnabled
        ) as AnimalSettingsResultV16;

        if (_notNil(newSettings.settings)) {
          setSettings(newSettings.settings);
        }
        setMetadata(_isNil(newSettings.metadata) ? availableMetadata : newSettings.metadata);
        setData(initialBulkAnimalDataV16(selectedAnimals, species) as Array<BulkAnimalRow>);
      } else {
        const newSettings = initAnimalSettings(
          species,
          true,
          removeRow,
          metaColumns,
          availableMetadata,
          undefined,
          entityFieldRequiredMap,
          unknownSexEnabled
        ) as AnimalSettingsResult;

        if (_notNil(newSettings.settings)) {
          setSettings(newSettings.settings);
        }
        setMetadata(_isNil(newSettings.metadata) ? availableMetadata : newSettings.metadata);
        setData(initialBulkAnimalData(selectedAnimals, species) as Array<BulkAnimalRow>);
      }
    }
  }, [
    initialLoad,
    selectedAnimals,
    species,
    availableMetadata,
    metaColumns,
    settings,
    entityFieldRequiredMap,
    hotUpgrade,
    unknownSexEnabled,
  ]);

  const patchMetadata = () => {
    if (_notNil(refV12.current)) {
      const hot = refV12.current.hotInstance;
      if (_notNil(hot)) {
        const col = hot.propToCol('species');
        const colData = hot.getDataAtCol(col);

        colData.forEach((value: string, row: number) => {
          updateStrainSelectDropdown(hot, species, row, value);
        });
        hot.render();
      }
    }
  };

  const afterChange = (changes: Array<CellChange> | null): void => {
    const hot = refV12?.current?.hotInstance;

    if (_notNil(changes)) {
      patchMetadata();
    }

    changes?.forEach((change) => {
      hot?.validateColumns(
        customValidators?.[hot.colToProp(change[1])]?.linkedValidationColumns?.map((linkedColumn) =>
          hot.propToCol(linkedColumn)
        ) ?? []
      );
    });
  };

  const backRoute = useMemo(
    () => webRoute(_notNil(studyId) ? 'studies.animals' : 'colony', { id: studyId }),
    [studyId]
  );

  const mapAnimalPatchData = (): Array<AnimalPatchData> => {
    const resolveStrainId = createStrainResolver(species);
    return data.map(({ id, name, sex, dob, species: animalSpecies, strain, meta }) => {
      const animal = selectedAnimals.find((a) => a.id === id);
      const returnData: AnimalPatchData = {
        id: toNumericId(id),
        cage_id: _notNil(animal?.cage_id) ? toNumericId(animal.cage_id) : null,
        name,
        dob: dob === '' ? null : dob,
        strain_id: resolveStrainId(animalSpecies, strain),
      };

      if (_notNil(sex)) {
        returnData.sex = AnimalSexLabelToCode[sex as AnimalSexLabel];
      }

      if (_isNotEmpty(meta) && _notNil(settings)) {
        if (hotUpgrade) {
          const v16Settings = settings as SpreadsheetV16Settings;
          if (_notNil(v16Settings.columns)) {
            const metaArray = createMetaV16(meta, v16Settings.columns);
            returnData.meta = metaArray.reduce<Record<string, MetadataFieldValueTypes>>((acc, item) => {
              acc[String(item.glossary_id)] = item.value;
              return acc;
            }, {});
          }
        } else {
          const v12Settings = settings as CustomGridSettings;
          if (_notNil(v12Settings.columns)) {
            returnData.meta = createMetaMap(meta, v12Settings.columns as Array<CustomColumnSettings>);
          }
        }
      }

      return returnData;
    });
  };

  const saveAnimals = async () => {
    setLoading(true);
    const animals = mapAnimalPatchData();
    try {
      const response = await ApiService.call({
        endpoint: 'PATCH /api/v1/animals',
        body: { animals },
        options: { onError: { throw: true, toast: false, slug: 'bulk-animals-update-many' } },
      });
      if (response.type === 'success') {
        navigate(backRoute);
        successToast(`Successfully added ${animals.length > 1 ? 'animals' : 'animal'}`);
      }
    } catch (error) {
      setApiError(error as Error);
    } finally {
      setLoading(false);
    }
  };

  const saveAction = async () => await saveAnimals();

  const spreadSheet = (
    <>
      {hotUpgrade ? (
        <Suspense fallback={<Loading />}>
          <div className="ow-spreadsheet-styles">
            <BulkAnimalsTableV16
              data={data as unknown as Array<Record<string, unknown>>}
              settings={settings as SpreadsheetV16Settings}
              innerRef={refV16 as React.RefObject<HotTableRef>}
              metadata={metadata}
              setMetadata={setMetadata}
              setInvalid={setIsInvalid}
              maxRows={data.length}
            />
          </div>
        </Suspense>
      ) : (
        <Suspense fallback={<Loading />}>
          <div className="ow-spreadsheet-styles">
            <SpreadSheet
              data={data}
              settings={settings as CustomGridSettings}
              className="pl4 pr4"
              innerRef={refV12}
              metadata={metadata}
              setMetadata={setMetadata}
              afterChange={afterChange}
              setInvalid={setIsInvalid}
              patchMetadataAfterSettingsUpdated={patchMetadata}
              maxRows={data.length}
              testPrefix="bulk-animals"
            />
          </div>
        </Suspense>
      )}
      <div className="pa4">
        <Button
          onClick={saveAction}
          disabled={loading || isInvalid}
          tooltip={isInvalid ? spreadSheetValidationTooltip : undefined}
          testId="bulk-animals__submit"
        >
          Save
        </Button>
        <Button plain className="ml3" onClick={() => navigate(backRoute)}>
          Cancel
        </Button>
      </div>
    </>
  );

  return (
    <div className="h-100">
      {apiError !== false && (
        <ApiErrorBanner
          className="mb4"
          title={'There was an error with your request'}
          text={
            'An error has occurred when submitting your request, please try again later. If this keeps occurring please contact support.'
          }
          error={apiError}
        />
      )}
      {loading || initialLoad || _isNil(settings) ? (
        <div style={{ height: 650 }}>
          <Loading txt={`${loading ? 'Updating animal...' : 'Loading...'}`} />
        </div>
      ) : (
        <>
          <SubHeader
            linkToText={_notNil(studyId) ? 'Animals' : 'Colony'}
            link={backRoute}
            testId="bulk-animals__return"
          />
          <div className="ph4">
            <Header mainHeaderText="Edit Animals" />
          </div>
          {_notNil(settings) && _isNotEmpty(data) ? (
            spreadSheet
          ) : (
            <div className="ma4">
              <NoDataCard
                title="You have not selected any animals"
                text={'Please select animals from inside Colony to proceed'}
                NoDataComponent={
                  <Link to={backRoute} className="link blue">
                    {`Go to ${_notNil(studyId) ? 'Animals' : 'Colony'}`}
                  </Link>
                }
              />
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default BulkAnimalEdit;
